import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

/** Public certificate verification page — reached by QR code scan. */
export default function VerifyCertificate() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/api/verify/${certId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "Certificate not found" : "Verification failed"));
  }, [certId]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] grid place-items-center p-6">
        <Card className="max-w-md" data-testid="verify-not-found">
          <CardContent className="p-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <div className="text-xl font-bold text-red-700 mb-1">{err}</div>
            <div className="text-sm text-gray-500">
              Please double-check the certificate ID you scanned or paste from the PDF.
            </div>
            <Link to="/" className="inline-block mt-4 text-sm text-blue-600 hover:underline">
              ← Back to Teacher4Me
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-center">Verifying…</div>;

  const issued = new Date(data.issued_at).toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#F7F5F0] py-12 px-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card className="border-emerald-200 border-2" data-testid="verify-success-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              <div>
                <div className="text-xl font-bold text-emerald-700">Certificate Authentic</div>
                <div className="text-sm text-gray-500">Verified by Teacher4Me.ca</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Student</div>
                <div className="text-2xl font-bold" data-testid="verify-student-name">
                  {data.student_name}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Course completed</div>
                <div className="text-lg font-semibold" data-testid="verify-course-title">
                  {data.course_title}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Issued</div>
                  <div className="font-medium" data-testid="verify-issued-at">{issued}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Certificate ID</div>
                  <div className="font-mono text-sm" data-testid="verify-cert-number">
                    {data.cert_number}
                  </div>
                </div>
              </div>

              {typeof data.final_score === "number" && (
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Final score</div>
                  <Badge className="bg-emerald-100 text-emerald-800 text-base">
                    {data.final_score}%
                  </Badge>
                </div>
              )}

              {data.is_dual_cert && (
                <div className="pt-4 border-t" data-testid="verify-dual-brand">
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-2 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Co-branded certificate
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500">Issued by</div>
                      <div className="font-semibold text-[#064E3B]">{data.partner_name}</div>
                    </div>
                    <div className="text-gray-400">+</div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500">In partnership with</div>
                      <div className="font-semibold text-[#D92D20]">Teacher4Me.ca</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-gray-500">
          This is a public verification page. Anyone with the certificate ID or QR code can
          confirm the details above match the issued certificate.
        </div>
      </div>
    </div>
  );
}
